import React, { useEffect, useState } from 'react';
import { AppBar, useAuthProvider, useNotify, useSidebarState } from 'react-admin';
import { Badge, Box, IconButton, Menu, MenuItem, Popover, Typography } from '@mui/material';
import './index.css';
import axiosApiInstance from 'src/api/axiosConfig';
import { ReactComponent as OsvCentr } from 'src/assets/osv-centr.svg';
import { ReactComponent as ChevronDown } from 'src/assets/chevron-down.svg';
import { ReactComponent as Ring } from 'src/assets/ring.svg';
import { palette, Theme } from '../../../Theme';
import UserMenu from 'src/components/UserMenu';
import { useNotification } from 'src/utils/notification';
import ShsAlert from 'src/components/common/ShsAlert';
import { WithRole } from 'src/components/common';
import { SpecialTeachersGroup, TeachersGroup } from 'src/constants/roles';
import { NotificationResponse } from 'src/components/common/ShsAlert/constants';
import { UserRole } from 'src/auth/types';

const HeaderStyles = {
    boxShadow: 'none',
    zIndex: 9,
    '& .RaAppBar-toolbar': {
        background: '#FFFFFF',
        borderBottom: `1px solid ${palette.primary.inputBorder}`,
        width: '100%',
        height: `${Theme.header.height}px`,
        display: 'flex',
        alignItems: 'center',
        padding: '0 20px',
        gap: '12px'
    },
    '& .RaAppBar-toolbar > .RaAppBar-menuButton': { display: 'none' },
    '& .RaAppBar-toolbar > .RaAppBar-title': { display: 'none' }
};

type EduCenter = {
    id: number;
    name: string;
};

export const Header = () => {
    const { changeCenter, getEducationCenterId } = useAuthProvider();
    const [sidebarOpen] = useSidebarState();
    const notify = useNotify();
    const notification = useNotification();

    const [anchorEl, setAnchorEl] = useState<any | null>(null);
    const [notifications, setNotifications] = useState<any[]>([]);
    const [checkedNotification, setCheckedNotification] = useState<any[]>([]);
    const [notificationCount, setNotificationCount] = useState<number>(
        Number(notification.getNotificationLengthFromLocalStorage()) || 0
    );

    const handleClick = (event: any) => {
        const data = notification.getNotification();
        const checked = notification.getLocalNotification();
        setNotifications(data.filter((x: any) => !checked.find((a: number) => a === x.userId)));
        setCheckedNotification(data.filter((x: any) => checked.find((a: number) => a === x.userId)));
        setNotificationCount(Number(notification.getNotificationLengthFromLocalStorage()) || 0);
        setAnchorEl(event.currentTarget);
    };

    const handleClickReadMarker = (id?: number) => {
        notification.changeNotificationReadStatus(id);
        setNotificationCount(Number(notification.getNotificationLengthFromLocalStorage()) || 0);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };
    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;

    const [eduCenterAnchorEl, setEduCenterAnchorEl] = useState<null | HTMLElement>(null);
    const eduCenterMenuOpen = Boolean(eduCenterAnchorEl);

    const [availableEduCenters, setAvailableEduCenters] = useState<EduCenter[]>([]);
    const eduCenter = availableEduCenters.find((x) => x.id === getEducationCenterId());
    const isMoreThenOneCenter = availableEduCenters.length > 1;

    useEffect(() => {
        notification.getNotification();
        axiosApiInstance.get('/centers/names').then((response) => setAvailableEduCenters(response.data));
    }, []);

    useEffect(() => {
        setNotificationCount(notification.getNotificationLengthFromLocalStorage());
    }, [notification.getNotificationLengthFromLocalStorage()]);

    const handleChangeEduCanter = (id: EduCenter['id']) => {
        changeCenter(id, true).catch(() => {
            notify('Невдалося змінити навчальний центр', {
                type: 'error',
                autoHideDuration: 2500
            });
        });
        setEduCenterAnchorEl(null);
    };

    const handleEduCenterMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setEduCenterAnchorEl(eduCenterMenuOpen ? null : event.currentTarget);
    };

    const handleEduCenterMenuClose = () => {
        setEduCenterAnchorEl(null);
    };

    const additionalLeftPadding = 20;

    return (
        <AppBar
            sx={{
                ...HeaderStyles,
                '& .RaAppBar-toolbar': {
                    ...HeaderStyles['& .RaAppBar-toolbar'],
                    paddingLeft: sidebarOpen
                        ? `${Theme.sidebar.width + additionalLeftPadding}px`
                        : `${Theme.sidebar.closedWidth + additionalLeftPadding}px`,
                    transition: (theme) =>
                        theme.transitions.create(['padding-left'], {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.leavingScreen
                        })
                }
            }}
            container="div"
            userMenu={false}>
            {eduCenter && (
                <WithRole roles={[...TeachersGroup, ...SpecialTeachersGroup, UserRole.ADMIN, UserRole.METHODOLOGIST]}>
                    <Box
                        onClick={isMoreThenOneCenter ? handleEduCenterMenuOpen : undefined}
                        sx={{
                            color: 'black',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            borderRadius: '8px',
                            padding: '1px 8px',
                            cursor: isMoreThenOneCenter ? 'pointer' : 'default',
                            maxWidth: '40%',
                            '&:hover': isMoreThenOneCenter ? { background: 'rgba(234, 233, 233, 0.671)' } : {}
                        }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                width: '42px' // This value should match width of user avatar to be consistent
                            }}>
                            <OsvCentr />
                        </Box>

                        <Typography
                            fontSize={16}
                            fontWeight={600}
                            lineHeight={1.0}
                            color={Theme.palette.primary.midnightBlue}>
                            {eduCenter.name}
                        </Typography>
                        {isMoreThenOneCenter && (
                            <ChevronDown
                                style={{
                                    transform: eduCenterMenuOpen ? 'rotate(180deg)' : 'none',
                                    transition: 'transform 0.2s',
                                    minWidth: '20px'
                                }}
                            />
                        )}
                    </Box>
                    {isMoreThenOneCenter && (
                        <Menu
                            anchorEl={eduCenterAnchorEl}
                            open={eduCenterMenuOpen}
                            onClose={handleEduCenterMenuClose}
                            autoFocus={false}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left'
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left'
                            }}
                            sx={{
                                '& .MuiPaper-root': {
                                    marginTop: '8px',
                                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
                                    borderRadius: '8px'
                                }
                            }}>
                            {availableEduCenters
                                .filter((x) => x.id !== eduCenter.id)
                                .map((center) => (
                                    <MenuItem
                                        key={center.id}
                                        onClick={() => handleChangeEduCanter(center.id)}
                                        sx={{
                                            padding: '10px 15px',
                                            maxWidth: '700px'
                                        }}>
                                        <Typography
                                            fontSize={16}
                                            fontWeight={500}
                                            style={{ textWrap: 'wrap' }}
                                            color={Theme.palette.primary.midnightBlue}>
                                            {center.name}
                                        </Typography>
                                    </MenuItem>
                                ))}
                        </Menu>
                    )}
                </WithRole>
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <IconButton size="large" color="primary" onClick={(e) => handleClick(e)}>
                    <Badge badgeContent={notificationCount >= 0 ? notificationCount : 0} color="error">
                        <Ring />
                    </Badge>
                </IconButton>
                <UserMenu />
            </Box>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                }}
                style={{
                    borderRadius: '8px',
                    maxHeight: '90%'
                }}>
                <Box
                    style={{
                        background: '#ffffff'
                    }}>
                    <Typography
                        fontSize={24}
                        fontWeight={700}
                        color={'#000000'}
                        position={'sticky'}
                        top={0}
                        sx={{
                            backgroundColor: '#ffffff'
                        }}
                        padding={'8px 16px'}>
                        Сповіщення
                    </Typography>
                    <Box
                        style={{
                            background: '#ffffff',
                            height: '80%',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '5px'
                        }}>
                        {notifications.length > 0 && (
                            <Typography
                                fontSize={18}
                                fontWeight={600}
                                color={'#000000'}
                                position={'sticky'}
                                top={'52px'}
                                sx={{
                                    backgroundColor: '#ffffff'
                                }}
                                padding={'0 16px'}>
                                Нові
                            </Typography>
                        )}
                        {notifications
                            .sort((a, b) => a.daysToExpiration - b.daysToExpiration)
                            .map((x: NotificationResponse) => (
                                <ShsAlert
                                    type={x.type}
                                    key={x.userId}
                                    name={x.name}
                                    surname={x.surname}
                                    patronymic={x.patronymic}
                                    userId={x.userId}
                                    daysToExpiration={x.daysToExpiration}
                                    medicalCheckupExpirationDate={x.medicalCheckupExpirationDate}
                                    handleClickMarker={handleClickReadMarker}
                                />
                            ))}
                        {checkedNotification.length > 0 && (
                            <Typography
                                fontSize={18}
                                fontWeight={600}
                                color={'#000000'}
                                position={'sticky'}
                                top={'52px'}
                                sx={{
                                    backgroundColor: '#ffffff'
                                }}
                                padding={'0 16px'}>
                                Переглянуті
                            </Typography>
                        )}
                        {checkedNotification
                            .sort((a, b) => a.daysToExpiration - b.daysToExpiration)
                            .map((x: NotificationResponse) => (
                                <ShsAlert
                                    type={x.type}
                                    key={x.userId}
                                    name={x.name}
                                    surname={x.surname}
                                    patronymic={x.patronymic}
                                    userId={x.userId}
                                    daysToExpiration={x.daysToExpiration}
                                    medicalCheckupExpirationDate={x.medicalCheckupExpirationDate}
                                    checked={true}
                                    handleClickMarker={handleClickReadMarker}
                                />
                            ))}
                        {notifications.length === 0 && (
                            <Box
                                width={'450px'}
                                padding={'20px 0'}
                                color={Theme.palette.primary.gray}
                                textAlign={'center'}>
                                Немає нових сповіщень
                            </Box>
                        )}
                    </Box>
                </Box>
            </Popover>
        </AppBar>
    );
};
